import Button from "@components/Button"
import Layout from "@components/Layout"
import {
  getActivityLevelFromLocalStorage,
  getHeightFromLocalStorage,
  getMainGoalFromLocalStorage,
  getWeightFromLocalStorage,
} from "@helpers/localStorage"
import { useTranslation } from "react-i18next"
import { Form, useSubmit } from "react-router-dom"

function calculateBmi(weightKg: number, heightCm: number): number {
  const heightM = heightCm / 100
  return weightKg / (heightM * heightM)
}

function getBmiCategory(
  bmi: number,
  t: (key: string) => string,
): { label: string; description: string } {
  if (bmi < 18.5)
    return {
      label: t("pages.onboarding.wellnessSnapshot.bmi.underweight"),
      description: t("pages.onboarding.wellnessSnapshot.bmi.underweightDesc"),
    }
  if (bmi < 25)
    return {
      label: t("pages.onboarding.wellnessSnapshot.bmi.normal"),
      description: t("pages.onboarding.wellnessSnapshot.bmi.normalDesc"),
    }
  if (bmi < 30)
    return {
      label: t("pages.onboarding.wellnessSnapshot.bmi.overweight"),
      description: t("pages.onboarding.wellnessSnapshot.bmi.overweightDesc"),
    }
  return {
    label: t("pages.onboarding.wellnessSnapshot.bmi.obese"),
    description: t("pages.onboarding.wellnessSnapshot.bmi.obeseDesc"),
  }
}

function getBmiBarPercent(bmi: number): number {
  const clamped = Math.max(15, Math.min(bmi, 40))
  return ((clamped - 15) / 25) * 100
}

const ACTIVITY_LEVEL_KEYS: Record<string, string> = {
  not_active: "pages.onboarding.wellnessSnapshot.activity.notActive",
  lightly_active: "pages.onboarding.wellnessSnapshot.activity.lightlyActive",
  moderately_active:
    "pages.onboarding.wellnessSnapshot.activity.moderatelyActive",
  very_active: "pages.onboarding.wellnessSnapshot.activity.veryActive",
}

const GOAL_LABEL_KEYS: Record<string, string> = {
  loseWeight: "pages.onboarding.wellnessSnapshot.goals.loseWeight",
  maintainWeight: "pages.onboarding.wellnessSnapshot.goals.maintainWeight",
  gainWeight: "pages.onboarding.wellnessSnapshot.goals.gainWeight",
  buildMuscle: "pages.onboarding.wellnessSnapshot.goals.buildMuscle",
  reduceBodyFat: "pages.onboarding.wellnessSnapshot.goals.reduceBodyFat",
  somethingElse: "pages.onboarding.wellnessSnapshot.goals.somethingElse",
}

const HELP_POINTS: { emoji: string; key: string }[] = [
  { emoji: "🍲", key: "pages.onboarding.wellnessSnapshot.help.recipes" },
  { emoji: "📊", key: "pages.onboarding.wellnessSnapshot.help.trackFood" },
  { emoji: "💧", key: "pages.onboarding.wellnessSnapshot.help.water" },
]

export default function WellnessSnapshot() {
  const { t } = useTranslation()
  const submit = useSubmit()

  const weight = parseFloat(getWeightFromLocalStorage() ?? "0")
  const height = parseFloat(getHeightFromLocalStorage() ?? "0")
  const activityLevel =
    getActivityLevelFromLocalStorage() ?? "moderately_active"
  const mainGoal = getMainGoalFromLocalStorage() ?? "loseWeight"

  const bmi = weight && height ? calculateBmi(weight, height) : 0
  const bmiCategory = getBmiCategory(bmi, t)
  const bmiPercent = getBmiBarPercent(bmi)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submit(event.currentTarget)
  }

  return (
    <Layout>
      <div className="flex grow flex-col items-center pt-4">
        <h1 className="text-custom-charcoal mb-6 text-center text-2xl font-extrabold italic sm:text-3xl">
          {t("pages.onboarding.wellnessSnapshot.heading")}
        </h1>

        {bmi > 0 && (
          <div className="mb-6 w-full rounded-2xl bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-500">
                {t("pages.onboarding.wellnessSnapshot.bmi.label")}
              </span>
              <span className="text-custom-charcoal text-xl font-bold">
                {bmi.toFixed(1)}
              </span>
            </div>

            <div className="relative mb-2 h-3 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="bg-custom-teal absolute inset-y-0 left-0 rounded-full transition-all"
                style={{ width: `${bmiPercent}%` }}
              />
              <div
                className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-gray-600 shadow"
                style={{ left: `${bmiPercent}%` }}
              />
            </div>

            <p className="text-center text-sm font-semibold text-gray-500">
              {bmiCategory.label}
            </p>
            <p className="text-center text-xs text-gray-400">
              {bmiCategory.description}
            </p>

            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
              <span className="text-sm text-gray-500">
                {t("pages.onboarding.wellnessSnapshot.activityLevel")}
              </span>
              <span className="text-custom-charcoal text-sm font-bold">
                {t(
                  ACTIVITY_LEVEL_KEYS[activityLevel] ??
                    ACTIVITY_LEVEL_KEYS.moderately_active,
                )}
              </span>
            </div>
          </div>
        )}

        <div className="mb-2 text-center">
          <p className="text-custom-charcoal text-sm font-bold">
            {t("pages.onboarding.wellnessSnapshot.yourMainGoal")}
          </p>
          <p className="text-custom-teal text-2xl font-extrabold">
            {t(GOAL_LABEL_KEYS[mainGoal] ?? GOAL_LABEL_KEYS.loseWeight)}
          </p>
        </div>

        <div className="w-full rounded-2xl bg-white p-5">
          <p className="text-custom-charcoal mb-3 text-center text-base font-bold">
            {t("pages.onboarding.wellnessSnapshot.weAlsoHelp")}
          </p>
          <div className="space-y-3">
            {HELP_POINTS.map(({ emoji, key }) => (
              <div key={key} className="flex items-center justify-center gap-3">
                <span className="text-xl">{emoji}</span>
                <span className="text-sm text-gray-600">{t(key)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 -mx-5 bg-gradient-to-t from-white from-70% to-transparent px-5 pt-8 pb-4">
        <Form method="post" onSubmit={handleSubmit}>
          <input name="wellnessSnapshot" type="hidden" value="continue" />
          <Button
            title={t("pages.onboarding.wellnessSnapshot.button")}
            type="submit"
          />
        </Form>
      </div>
    </Layout>
  )
}
